import React, { useRef } from "react"
import toast from "react-hot-toast"
import EndOfYearReflectionForm from "./EndOfYearReflectionForm"
import FormActivityLog from "./FormActivityLog"
import {
  FORM_UPLOAD_ACCEPT,
  FORM_UPLOAD_DISCLAIMER,
  validateFormUploadFiles,
} from "../../utils/formUploadConstraints"

export {
  EMPTY_END_OF_YEAR_FORM,
  mapEndOfYearCheckInToForm,
} from "./EndOfYearReflectionForm"

const FORM_LINK_CLASS = "text-blue-700 underline cursor-pointer"
const TEXTAREA_CLASS =
  "w-full min-h-[120px] resize-y border border-gray-300 rounded p-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#241763]/30 focus:border-[#241763]"
const TEXTAREA_READ_ONLY_CLASS =
  "w-full min-h-[120px] resize-y border border-gray-200 rounded p-2 text-sm bg-gray-50 text-gray-700"
const INPUT_CLASS =
  "w-full border border-gray-300 rounded px-3 py-2.5 text-sm leading-normal bg-white focus:outline-none focus:ring-2 focus:ring-[#241763]/30 focus:border-[#241763]"
const INPUT_READ_ONLY_CLASS =
  "w-full border border-gray-200 rounded px-3 py-2.5 text-sm leading-normal bg-gray-50 text-gray-700"

export const EMPTY_CHECK_IN_FORM = {
  leaderName: "",
  leaderLocation: "",
  selfReflection: "",
  evidenceAttachments: [],
  conferenceDate: "",
  feedbackToLeader: "",
}

export const mapCheckInToForm = (checkIn) => {
  let evidenceAttachments = []
  try {
    evidenceAttachments = checkIn?.evidenceAttachments
      ? JSON.parse(checkIn.evidenceAttachments)
      : []
  } catch {
    evidenceAttachments = []
  }

  return {
    leaderName: checkIn?.leaderName || checkIn?.adminName || "",
    leaderLocation: checkIn?.leaderLocation || "",
    selfReflection: checkIn?.selfReflection || "",
    evidenceAttachments,
    conferenceDate: checkIn?.conferenceDate || "",
    feedbackToLeader: checkIn?.feedbackToLeader || "",
  }
}

const CHECK_IN_CONFIG = {
  mid_year: {
    title: "Mid-Year Check-in: Reflection, Adjustment(s), & Next Steps",
    conferenceTitle: "Mid-Year Conference",
    conferenceSubtitle: "Completed by Evaluator (by Mid-February)",
    reflectionPrompt:
      "What has been your progress to-date on your professional learning plan and your goal(s) and how do you know? What are your next steps and why?",
  },
  end_of_year: {
    title: "End-of-Year Reflection",
    conferenceTitle: "End-of-Year Conference",
    conferenceSubtitle: "Completed by Evaluator (by Mid June)",
    reflectionPrompt:
      "Reflect on your progress toward your annual goals. What evidence demonstrates your growth, what adjustments were made, and what are your key takeaways for next year?",
  },
}

const AdminCheckInForm = ({
  formData,
  onChange,
  mode = "admin",
  checkInType = "mid_year",
  adminName,
  onUploadEvidence,
  uploadingEvidence = false,
  record = null,
}) => {
  const fileInputRef = useRef(null)
  const config = CHECK_IN_CONFIG[checkInType] || CHECK_IN_CONFIG.mid_year
  const completedByName = adminName || formData.leaderName || "Name"

  const isLeaderReadOnly =
    mode === "super_admin_conference" || mode === "view" || mode === "initiate"
  const isConferenceReadOnly = mode === "admin" || mode === "initiate" || mode === "view"

  const handleFileChange = async (event) => {
    const files = Array.from(event.target.files || [])
    if (!files.length || !onUploadEvidence) return

    const validationError = validateFormUploadFiles(files)
    if (validationError) {
      toast.error(validationError)
      event.target.value = ""
      return
    }

    await onUploadEvidence(files)
    event.target.value = ""
  }

  const handleRemoveAttachment = (index) => {
    const updated = formData.evidenceAttachments.filter((_, i) => i !== index)
    onChange("evidenceAttachments", updated)
  }

  if (checkInType === "end_of_year") {
    return (
      <EndOfYearReflectionForm
        formData={formData}
        onChange={onChange}
        mode={mode}
        adminName={adminName}
        onUploadEvidence={onUploadEvidence}
        uploadingEvidence={uploadingEvidence}
        record={record}
      />
    )
  }

  return (
    <div className="space-y-4">
      {adminName && (
        <p className="text-sm text-gray-600">
          {config.title} for:{" "}
          <span className="font-semibold">{adminName}</span>
        </p>
      )}

      <div className="border border-gray-400 overflow-hidden">
        <div className="bg-[#5B9BD5] text-center font-bold py-2 border-b border-gray-400 text-sm text-white">
          {config.title}
        </div>
        <div className="bg-[#D9EAF7] text-center py-2 border-b border-gray-400 text-sm italic">
          Completed by {completedByName}
        </div>

        <table className="w-full border-collapse">
          <tbody>
            <tr className="border border-gray-400">
              <td className="w-1/2 border border-gray-400 p-3 text-sm font-medium bg-white">
                Name:
              </td>
              <td className="w-1/2 border border-gray-400 p-2 bg-white">
                <input
                  type="text"
                  value={formData.leaderName}
                  onChange={(e) => onChange("leaderName", e.target.value)}
                  readOnly={isLeaderReadOnly}
                  className={isLeaderReadOnly ? INPUT_READ_ONLY_CLASS : INPUT_CLASS}
                />
              </td>
            </tr>
            <tr className="border border-gray-400">
              <td className="w-1/2 border border-gray-400 p-3 text-sm font-medium bg-white">
                Location:
              </td>
              <td className="w-1/2 border border-gray-400 p-2 bg-white">
                <input
                  type="text"
                  value={formData.leaderLocation}
                  onChange={(e) => onChange("leaderLocation", e.target.value)}
                  readOnly={isLeaderReadOnly}
                  className={isLeaderReadOnly ? INPUT_READ_ONLY_CLASS : INPUT_CLASS}
                />
              </td>
            </tr>
            <tr className="border border-gray-400">
              <td className="w-[38%] align-top border border-gray-400 p-3 text-sm bg-white">
                {config.reflectionPrompt}
              </td>
              <td className="w-[62%] align-top border border-gray-400 p-2 bg-white">
                <div className="font-medium text-sm mb-2">Self-Reflection:</div>
                <textarea
                  value={formData.selfReflection}
                  onChange={(e) => onChange("selfReflection", e.target.value)}
                  readOnly={isLeaderReadOnly}
                  rows={6}
                  className={
                    isLeaderReadOnly ? TEXTAREA_READ_ONLY_CLASS : TEXTAREA_CLASS
                  }
                />
              </td>
            </tr>
            <tr className="border border-gray-400">
              <td className="w-[38%] align-top border border-gray-400 p-3 text-sm bg-white font-medium">
                Links to Evidence:
              </td>
              <td className="w-[62%] align-top border border-gray-400 p-3 bg-white">
                {!isLeaderReadOnly && (
                  <div className="mb-3">
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept={FORM_UPLOAD_ACCEPT}
                      onChange={handleFileChange}
                      className="hidden"
                      aria-label="Upload evidence files"
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingEvidence}
                      className="px-4 py-2 text-sm bg-[#241763] text-white rounded hover:bg-[#1a1047] disabled:opacity-50"
                    >
                      {uploadingEvidence ? "Uploading..." : "Upload Attachments"}
                    </button>
                    <p className="text-xs text-gray-500 mt-2">
                      {FORM_UPLOAD_DISCLAIMER}
                    </p>
                  </div>
                )}

                {formData.evidenceAttachments?.length > 0 ? (
                  <ul className="space-y-2 overflow-visible">
                    {formData.evidenceAttachments.map((file, index) => (
                      <li
                        key={`${file.fileUrl}-${index}`}
                        className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between text-sm border border-gray-200 rounded p-3 bg-white overflow-visible"
                      >
                        <a
                          href={file.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="evidence-link text-blue-700 underline break-all leading-normal py-1 hover:text-blue-900"
                          aria-label={`Open evidence ${file.fileName || "file"}`}
                          onClick={(event) => {
                            if (!file.fileUrl) {
                              event.preventDefault()
                              toast.error("Evidence file link is unavailable")
                              return
                            }
                            event.preventDefault()
                            window.open(file.fileUrl, "_blank", "noopener,noreferrer")
                          }}
                        >
                          {file.fileName || "View evidence"}
                        </a>
                        {!isLeaderReadOnly && (
                          <button
                            type="button"
                            onClick={() => handleRemoveAttachment(index)}
                            className="text-red-600 text-xs shrink-0 self-start sm:self-center"
                            aria-label={`Remove ${file.fileName || "evidence"}`}
                          >
                            Remove
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-500">No evidence uploaded yet.</p>
                )}
              </td>
            </tr>
          </tbody>
        </table>

        <div className="bg-[#5B9BD5] text-center py-2 border-t border-gray-400 text-white">
          <div className="font-bold text-sm">{config.conferenceTitle}</div>
          <div className="italic text-sm">{config.conferenceSubtitle}</div>
          <div className="italic text-sm">Date</div>
        </div>

        <div className="border border-gray-400 p-3 bg-white">
          <div className="font-bold text-sm mb-2">
            Feedback to Leader (Feedback regarding progress on professional learning
            and progress toward goal(s). Include change in tiered supports, if
            recommended.):
          </div>
          <textarea
            value={formData.feedbackToLeader}
            onChange={(e) => onChange("feedbackToLeader", e.target.value)}
            readOnly={isConferenceReadOnly}
            rows={8}
            placeholder={
              isConferenceReadOnly
                ? "To be completed by Super Admin after admin submission"
                : ""
            }
            className={`w-full min-h-[160px] resize-y rounded p-2 text-sm border ${
              isConferenceReadOnly
                ? "bg-gray-50 text-gray-500 border-gray-200"
                : "bg-white border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#241763]/30 focus:border-[#241763]"
            }`}
          />
        </div>
      </div>

      <FormActivityLog record={record} />
    </div>
  )
}

export default AdminCheckInForm
